import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import {AnimationType, DRILL_IN_EVENT_TYPE, RESELECT_ITEM_EVENT_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import {getUniqueString} from '@Utils/string.util';
import {PageItems} from '@PosterWhiteboard/page/page-items.class';
import type {
  GalleryTemplateDataObject,
  PageEvents,
  PageModifiedEvent,
  PageObject,
  PageUpdateFromObjectOpts,
  ToDataURLOpts,
  TemplateDataObject,
} from '@PosterWhiteboard/page/page.types';
import {
  PAGE_SLIDER_CONTAINER_HEIGHT,
  CANVAS_BACKDROP_CLASS,
  PAGE_CONTAINER_CLASS,
  INNER_PAGE_CONTAINER_CLASS,
  ACTIVE_PAGE_CLASS,
  ACTIVE_PAGE_FLASH_CLASS,
  EMPTY_BACKGROUND_HOVER_CLASS,
  TRANSPARENT_CANVAS_COLOR,
  OPAQUE_CANVAS_COLOR,
} from '@PosterWhiteboard/page/page.types';
import {PAGE_END_TIME_TOLERANCE_SECONDS} from '@PosterWhiteboard/poster/poster.types';
import {initTransformDetail} from '@PosterWhiteboard/page/item-transform-details';
import {RenderBleed} from '@PosterWhiteboard/page/render-bleed.class';
import {RenderGrid} from '@PosterWhiteboard/page/render-grid.class';
import {RenderAlignmentGuidelines} from '@PosterWhiteboard/page/render-alignment-guidelines.class';
import {RenderFold} from '@PosterWhiteboard/page/render-fold.class';
import {PageSpellCheck} from '@PosterWhiteboard/page/page-spellcheck.class';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {SelectionManager} from '@PosterWhiteboard/page/active-selection.class';
import {GroupDrillIn} from '@PosterWhiteboard/page/group-drill-in.class';
import type {RGBA} from '@Utils/color.util';
import {rgbaToHex} from '@Utils/color.util';
import {PageClipboard} from '@PosterWhiteboard/page/page-clipboard';
import {PageSnapshot} from '@PosterWhiteboard/page/page-snapshot.class';
import {PageSVG} from '@PosterWhiteboard/page/page-svg.class';
import {PageWatermark} from '@PosterWhiteboard/page/page-watermark.class';
import {handleTextSelectionPopupMenu, updateTextSelection} from '@Libraries/text-selection-library';
import {GA4EventName, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {isMobile} from 'react-device-detect';
import type {UpdateShouldHideControlsProps} from '@Components/poster-editor/poster-editor-reducer';
import {CanvasPanOnTextEdit} from '@PosterWhiteboard/page/page-canvas-pan-on-text-edit.class';
import {getFabricObjectUID, type ItemFabricObject} from '@PosterWhiteboard/items/item/item.class';
import {redirectUser} from '@Utils/browser.util';
import type {CanvasEvents, Group, TMat2D} from '@postermywall/fabricjs-2';
import type {FabricObject} from '@postermywall/fabricjs-2';
import {ActiveSelection, Canvas, Point, StaticCanvas, Textbox, util} from '@postermywall/fabricjs-2';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import Emitter from '@/services/event-emitter';
import type {DeepPartial} from '@/global';
import type {CornerPoints} from '@/utils/math.util';
import 'hammerjs';
import {noop} from '@Utils/general.util';
import {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';
import tinycolor from 'tinycolor2';
import {Observable} from '@PosterWhiteboard/observable';
import {cloneAsAlignedImage} from '@Utils/fabric.util';
import {doItemsCarryAuthoredTimings, isPageVideo} from '@Libraries/page-object.library';
import {combinePageDuration, WEBPAGE_STATIC_PAGE_DURATION} from '@Libraries/page-duration.library';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {AudioClips} from '@PosterWhiteboard/classes/audio-clips/audio-clips.class';
import {PageTransitions} from '@PosterWhiteboard/page/transitions/page-transitions.class';
import {remapPageTransitionsObjectUids} from '@PosterWhiteboard/page/transitions/item-transition-factory';
import {isColorBackground, isVideoBackground} from '@PosterWhiteboard/page/background/background.types';
import {applyCanvasCursor, setCanvasCursor} from '@Utils/setCanvasCursor';
import {Clock} from '@PosterWhiteboard/clock/clock.class';
import type {AnimationObject} from '@PosterWhiteboard/classes/item-animation.class';
import {getAnimationTime} from '@PosterWhiteboard/classes/item-animation.class';
import {isAnimationApplicableOnItem} from '@PosterWhiteboard/animation/animation.class';
import {calculateIntroStartTimes, calculateOutroEndTimes, isAnimationDurationApplicableOnItem} from '@PosterWhiteboard/page/page-animation.util';
import {DEFAULT_NON_VIDEO_PAGE_DURATION} from '@PosterWhiteboard/libraries/video-page';
import {clampGroupDeltaToFloor, clampGroupRowDelta} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import type {TimelineResizeHandle} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import {commitPosterMutation} from '@PosterWhiteboard/poster/commit-poster-mutation';

export interface AddItemOpts {
  undoable?: boolean;
  updateRedux?: boolean;
  fireEvent?: boolean;
  zIndex?: number;
  /**
   * Skip the post-add sub-row recalc. Used when reconciling a full serialized set
   * (load, undo/redo): recalcing after each add sees only a partial item set, so its
   * gap-compaction permutes the saved row assignments. The reconciler runs a single
   * recalc once every item is in place instead.
   */
  skipSubRowRecalc?: boolean;
}

interface PageConstructorOpts {
  hashedID?: string;
  order?: number;
  zoom?: number;
}

export class Page extends Observable<PageEvents> {
  public hashedID = '';
  public fabricCanvas!: Canvas | StaticCanvas;
  public fabricCanvasHammerManager?: HammerManager;
  public hammerEventFunctions;
  public items!: PageItems;
  public poster: Poster;
  public audioClips: AudioClips;
  public transitions: PageTransitions;
  public isCustomDuration = false;
  public renderAlignmentGuidelines: RenderAlignmentGuidelines;
  public renderBleed: RenderBleed;
  public renderGrid: RenderGrid;
  public renderFold: RenderFold;
  public pageWatermark: PageWatermark;
  public pageSnapshot: PageSnapshot;
  public pageSVG: PageSVG;
  public spellCheck: PageSpellCheck;
  public clipboard: PageClipboard;
  public activeSelection: SelectionManager;
  public canvasPanOnTextEdit: CanvasPanOnTextEdit;
  public longPressInitiated: boolean;
  public appliedPalette?: ColorPaletteInfo;
  public lastModified: Date;
  private mouseOverObject: FabricObject | null = null;
  // Viewport point where a pointer-down on the background/empty canvas began, while it could
  // still become a marquee. Undefined once a drag is confirmed or the gesture ends.
  private marqueePointerDownPoint: Point | undefined;
  // True once a marquee drag is actually underway (box dragged past the threshold). Suppresses
  // hover borders for the rest of the gesture.
  private isDragSelectInProgress = false;
  // Set on a pointer-down over the empty canvas of the already-active page when no background
  // exists, so a later clean click (not a marquee drag) can add and select a default background.
  private emptyBackgroundClickCandidate = false;
  private isDisposed = false;
  #isSeeking = false;
  #pendingSeekTime: number | undefined = undefined;
  private isSuspended = false;
  // Full page object whose items/audioClips were skipped during the initial poster load and are
  // loaded lazily. While set, toObject() serializes from it so save/undo never see a partial page.
  private deferredPageContentObject: PageObject | null = null;
  private deferredPageContentLoadPromise: Promise<void> | null = null;
  private isApplyingDeferredPageContent = false;
  public groupDrillIn = new GroupDrillIn(this);
  private readonly boundOnWindowFocus = (): void => {
    this.fabricCanvas.requestRenderAll();
  };

  private readonly boundOnUserLoggedIn = this.onUserLoggedIn.bind(this);
  private pauseTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private activeFlashTimeoutId: ReturnType<typeof setTimeout> | null = null;
  public isPlaying = false;
  public clock: Clock;
  public pageContainer!: HTMLDivElement;
  public canvasBackdrop!: HTMLDivElement;
  public canvasContainer!: HTMLDivElement | HTMLCanvasElement;
  public templateData: TemplateDataObject | null = null;
  public galleryTemplateData: GalleryTemplateDataObject | null = null;

  public constructor(poster: Poster, opts: PageConstructorOpts = {}) {
    super();
    this.poster = poster;
    this.hashedID = opts.hashedID ?? getUniqueString();
    this.items = new PageItems(this);
    this.audioClips = new AudioClips(this);
    this.transitions = new PageTransitions(this);
    this.activeSelection = new SelectionManager(this);
    this.canvasPanOnTextEdit = new CanvasPanOnTextEdit(this);
    this.longPressInitiated = false;
    this.hammerEventFunctions = {
      longpress: this.openTouchContextualMenu.bind(this),
      singletap: this.onSingleTapOnCanvas.bind(this),
      doubletap: this.onCanvasDoubleTap.bind(this),
    };

    this.addCanvasToPoster(opts);
    this.renderAlignmentGuidelines = new RenderAlignmentGuidelines(this);
    this.renderBleed = new RenderBleed(this);
    this.renderGrid = new RenderGrid(this);
    this.renderFold = new RenderFold(this);
    this.pageWatermark = new PageWatermark(this);
    this.pageSnapshot = new PageSnapshot(this);
    this.pageSVG = new PageSVG(this);
    if (this.fabricCanvas instanceof Canvas) {
      initTransformDetail(this.fabricCanvas);
    }
    this.spellCheck = new PageSpellCheck(this);
    this.clipboard = new PageClipboard(this);
    Emitter.on('user:loggedIn', this.boundOnUserLoggedIn);

    this.on('modified', this.onPageModified.bind(this));
    this.on('time:updated', this.onPageTimeUpdated.bind(this));
    this.lastModified = new Date();
    this.clock = new Clock(() => this.getPlaybackDuration(), {
      onEnd: this.onPageClockEnd.bind(this),
      onTimeUpdated: (time: number): void => {
        this.fire('time:updated', time);
        this.poster.redux.updatePageData({pageHashId: this.hashedID, obj: {currentTime: time}});
      },
    });
  }

  private async onPageClockEnd(): Promise<void> {
    await this.pause();
    this.fire('playback:ended');
  }

  public refreshVisibleCanvas(preemptiveRenderAheadBuffer = 0): void {
    if (this.poster.isRefreshVisibleCanvasDisabled) {
      return;
    }

    if (this.isSuspended) {
      return;
    }

    const whiteboardContainer = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get(0);
    if (!whiteboardContainer || !this.canvasBackdrop || !this.canvasContainer) return;

    const whiteboardContainerRect = whiteboardContainer.getBoundingClientRect();
    const backdropRect = this.canvasBackdrop.getBoundingClientRect();

    let didResizeCanvas = false;

    if (this.poster.mode.isGeneration()) {
      this.canvasContainer.style.top = `0px`;
      this.canvasContainer.style.left = `0px`;
      // this.canvasBackdrop.getBoundingClientRect() returns all-zero when the element is inside a display:none
      // ancestor (which happens when onInit fires before React re-renders to remove _hidden).
      // In generation mode the backdrop is always poster.width * zoom, so use that directly.
      const zoom = this.fabricCanvas.getZoom();
      this.fabricCanvas.setDimensions({
        width: this.poster.width * zoom,
        height: this.poster.height * zoom,
      });
    } else {
      const backdropVisibleTop = Math.max(backdropRect.top, whiteboardContainerRect.top - preemptiveRenderAheadBuffer);
      const backdropVisibleBottom = Math.min(backdropRect.bottom, whiteboardContainerRect.bottom + preemptiveRenderAheadBuffer);
      const backdropVisibleHeight = backdropVisibleBottom - backdropVisibleTop;

      const backdropVisibleLeft = Math.max(backdropRect.left, whiteboardContainerRect.left - preemptiveRenderAheadBuffer);
      const backdropVisibleRight = Math.min(backdropRect.right, whiteboardContainerRect.right + preemptiveRenderAheadBuffer);
      const backdropVisibleWidth = backdropVisibleRight - backdropVisibleLeft;

      if (backdropVisibleHeight <= 0 || backdropVisibleWidth <= 0) {
        return;
      }

      const backdropOffsetY = backdropVisibleTop - backdropRect.top;
      const backdropOffsetX = backdropVisibleLeft - backdropRect.left;

      const vpt: TMat2D = [...this.fabricCanvas.viewportTransform];
      vpt[4] = -backdropOffsetX;
      vpt[5] = -backdropOffsetY;
      this.canvasContainer.style.top = `${backdropOffsetY}px`;
      this.canvasContainer.style.left = `${backdropOffsetX}px`;
      this.fabricCanvas.viewportTransform = vpt;
      this.fabricCanvas.setViewportTransform(vpt);

      if (this.fabricCanvas.width !== backdropVisibleWidth || this.fabricCanvas.height !== backdropVisibleHeight) {
        this.fabricCanvas.setDimensions({
          width: backdropVisibleWidth,
          height: backdropVisibleHeight,
        });
        didResizeCanvas = true;
      }
    }

    // `setDimensions` clears the canvas, and the backdrop/container has already resized synchronously
    // (e.g. a mobile bottom-sheet panel closing fits-to-screen on every ResizeObserver tick). Deferring
    // the repaint to a rAF leaves a blank/stale frame in between — the flicker. Repaint synchronously
    // on a resize so the canvas content tracks the new size in the same tick; plain scroll/playback
    // (no dimension change) keep the cheaper batched render.
    if (didResizeCanvas) {
      this.fabricCanvas.renderAll();
    } else {
      this.fabricCanvas.requestRenderAll();
    }
  }

  public refreshFullCanvas(): void {
    if (!this.canvasBackdrop || !this.canvasContainer) return;

    const backdropRect = this.canvasBackdrop.getBoundingClientRect();

    this.canvasContainer.style.top = '0px';
    this.canvasContainer.style.left = '0px';

    const vpt: TMat2D = [...this.fabricCanvas.viewportTransform];
    vpt[4] = 0;
    vpt[5] = 0;
    this.fabricCanvas.viewportTransform = vpt;
    this.fabricCanvas.setViewportTransform(vpt);

    if (this.isCanvasDimensionsDifferentFromBackdrop(backdropRect)) {
      this.fabricCanvas.setDimensions({
        width: backdropRect.width,
        height: backdropRect.height,
      });
    }

    this.fabricCanvas.requestRenderAll();
  }

  public async addItem(newItem: ItemType, {updateRedux = true, fireEvent = true, undoable = true, zIndex, skipSubRowRecalc = false}: AddItemOpts = {}): Promise<void> {
    newItem.page = this;
    this.items.addItem(newItem);
    const targetIndex = this.resolveItemInsertIndex(newItem, zIndex);
    if (targetIndex !== undefined) {
      this.fabricCanvas.insertAt(targetIndex, newItem.fabricObject);
    } else {
      this.fabricCanvas.add(newItem.fabricObject);
    }
    await newItem.onItemAddedToPage();
    if (!skipSubRowRecalc) {
      this.items.recalculateTimelineSubRows();
    }
    if (!this.poster.mode.isGeneration()) {
      this.fabricCanvas.requestRenderAll();
    }

    if (undoable) {
      this.poster.history.addPosterHistory();
    }
    if (fireEvent) {
      this.fire('modified');
    }
    if (updateRedux) {
      this.poster.redux.updateReduxData();
    }
  }

  public isMemoryIntensive(): boolean {
    const amountOfHeavyItems = this.items.getImageItems().length + this.items.getSlideshowItems().length + this.items.getVideoItems().length;
    if (this.isVideo()) {
      return amountOfHeavyItems > 5;
    }
    return amountOfHeavyItems > 30;
  }

  private resolveItemInsertIndex(newItem: ItemType, requestedZIndex?: number): number | undefined {
    const fabricObjects = this.fabricCanvas.getObjects();
    const totalCount = fabricObjects.length;
    let bgCount = 0;
    for (const obj of fabricObjects) {
      const existing = obj.__PMWID ? this.items.itemsHashMap[obj.__PMWID] : undefined;
      if (existing?.isBackground) {
        bgCount += 1;
      }
    }

    if (newItem.isBackground) {
      return bgCount;
    }
    if (requestedZIndex === undefined) {
      return undefined;
    }
    if (requestedZIndex < bgCount) {
      return bgCount;
    }
    if (requestedZIndex > totalCount) {
      return totalCount;
    }
    return requestedZIndex;
  }

  private onUserLoggedIn(): void {
    this.pageWatermark.refreshWatermark();
  }

  private isCanvasDimensionsDifferentFromBackdrop(backdropRect: DOMRect): boolean {
    return this.fabricCanvas.width !== backdropRect.width || this.fabricCanvas.height !== backdropRect.height;
  }

  public getNonScaledCanvasScaleddWidth(): number {
    return this.fabricCanvas.width / this.fabricCanvas.getZoom();
  }

  public getNonScaledCanvasScaleddHeight(): number {
    return this.fabricCanvas.height / this.fabricCanvas.getZoom();
  }

  /**
   * Commits a timeline group drag — item and audio members moving by ONE shared time
   * delta, in ONE history entry. Lives on Page because the group spans two owners
   * (PageItems and AudioClips): each owner applies its own bars without committing,
   * the page commits once. The time delta is clamped jointly so the earliest member
   * blocks the whole group at the timeline start. Row deltas are PER TRACK — rows are
   * track-local coordinates, so the gesture's vertical movement only applies to the
   * grabbed bar's track (matching the preview, which never floats cross-track members
   * vertically) — and are clamped so the group stays within the track's existing rows.
   */
  public moveTimelineGroup({
    itemUids,
    audioUids,
    deltaTime,
    itemRowDelta = 0,
    audioRowDelta = 0,
  }: {
    itemUids: string[];
    audioUids: string[];
    deltaTime: number;
    itemRowDelta?: number;
    audioRowDelta?: number;
  }): void {
    const items = itemUids.map((uid) => this.items.itemsHashMap[uid]).filter((item) => item !== undefined);
    const audioItems = audioUids.map((uid) => this.audioClips.audioItemsHashMap[uid]).filter((audioItem) => audioItem !== undefined);
    if (items.length === 0 && audioItems.length === 0) {
      return;
    }
    const clampedDelta = clampGroupDeltaToFloor(deltaTime, [...items.map((item) => item.onPageStartTime), ...audioItems.map((audioItem) => audioItem.onPageStartTime)]);
    const clampedItemRowDelta = clampGroupRowDelta(
      itemRowDelta,
      items.map((item) => item.timelineSubRow),
      this.#getMaxItemTrackSubRow()
    );
    const clampedAudioRowDelta = clampGroupRowDelta(
      audioRowDelta,
      audioItems.map((audioItem) => audioItem.timelineSubRow),
      this.#getMaxAudioTrackSubRow()
    );
    if (clampedDelta === 0 && clampedItemRowDelta === 0 && clampedAudioRowDelta === 0) {
      return;
    }
    this.items.applyGroupTimelineMove(itemUids, clampedDelta, clampedItemRowDelta);
    this.audioClips.applyGroupTimelineMove(audioUids, clampedDelta, clampedAudioRowDelta);
    commitPosterMutation(this);
  }

  /**
   * Clears the track-owned timeline selections (audio, subtitle, transition boundary) —
   * the timeline's empty-area click. Canvas item selection is fabric's own and is cleared
   * by canvas interactions, not timeline empty-clicks.
   */
  public clearTimelineTrackSelections(): void {
    this.audioClips.unselectAudioPlaylist();
    this.items.clearSelectedSubtitle();
    this.transitions.clearBoundarySelection();
  }

  /**
   * Splits every given bar at `splitTime` — canvas items/backgrounds and audio clips —
   * in ONE history entry. Lives on Page because the bars span two owners; each owner
   * applies its splits without committing, the page commits once. Bars whose uid no
   * longer resolves are skipped; nothing commits when no bar actually split. The
   * originals keep their uids as the left halves, so an existing selection ends up on
   * the left pieces.
   */
  public async splitTimelineBars({itemUids, audioUids, splitTime}: {itemUids: string[]; audioUids: string[]; splitTime: number}): Promise<void> {
    let didSplitAnyBar = false;
    // Sequential on purpose: item splits add items (deterministic z-order) and audio
    // splits reinit players against the shared Howler pool — and selections are small.
    for (const uid of itemUids) {
      // eslint-disable-next-line no-await-in-loop
      didSplitAnyBar = (await this.items.applySplit(uid, splitTime)) || didSplitAnyBar;
    }
    for (const uid of audioUids) {
      // eslint-disable-next-line no-await-in-loop
      didSplitAnyBar = (await this.audioClips.applySplit(uid, splitTime)) || didSplitAnyBar;
    }
    if (didSplitAnyBar) {
      commitPosterMutation(this);
    }
  }

  /**
   * Cross-track conversion commits — a background bar dropped on the item track
   * becomes a normal item (and vice versa), in ONE history entry each. The heavy
   * lifting (rebuild through the factory, sequence re-tiling, row racking) lives in
   * PageItems; the page owns the commit. Like a same-track drag, releasing selects
   * the dropped bar — here under its NEW identity, which is why the drag hook skips
   * its own post-drag re-select for converted bars.
   */
  public async convertBackgroundBarToItem(uid: string, drop: {startTime: number; subRow: number; insertNewRow?: boolean}): Promise<void> {
    if (await this.items.convertBackgroundToItem(uid, drop)) {
      commitPosterMutation(this);
      const convertedItem = this.items.itemsHashMap[uid];
      if (convertedItem) {
        this.activeSelection.setActiveObject(convertedItem.fabricObject);
      }
    }
  }

  public async convertItemBarToBackground(uid: string, insertionIndex: number): Promise<void> {
    if (await this.items.convertItemToBackground(uid, insertionIndex)) {
      commitPosterMutation(this);
      await this.selectBackground(uid);
    }
  }

  #getMaxItemTrackSubRow(): number {
    const rows = Object.values(this.items.itemsHashMap)
      .filter((item) => item.isItemForTimelineItemTrack())
      .map((item) => item.timelineSubRow);
    return rows.length > 0 ? Math.max(...rows) : 0;
  }

  #getMaxAudioTrackSubRow(): number {
    const rows = Object.values(this.audioClips.audioItemsHashMap).map((audioItem) => audioItem.timelineSubRow);
    return rows.length > 0 ? Math.max(...rows) : 0;
  }

  /**
   * Commits a timeline group resize: the grabbed edge's delta is applied to that same
   * edge of every member, clamped per member by its own resize bounds (items by
   * MIN_ITEM_DURATION and the timeline start, trimmed media by its available source) —
   * members that can't absorb the full delta stop at their own limit. One history
   * entry for the whole gesture.
   */
  public async resizeTimelineGroup({itemUids, audioUids, handle, deltaTime}: {itemUids: string[]; audioUids: string[]; handle: TimelineResizeHandle; deltaTime: number}): Promise<void> {
    if (deltaTime === 0 || itemUids.length + audioUids.length === 0) {
      return;
    }
    // Audio first: its trim changes await player reinits, and item mutations must not
    // sit uncommitted across those awaits — items apply synchronously right before the commit.
    await this.audioClips.applyGroupTimelineResize(audioUids, handle, deltaTime);
    this.items.applyGroupTimelineResize(itemUids, handle, deltaTime);
    commitPosterMutation(this);
  }

  public isExpensiveVideo(): boolean {
    return this.items.hasVideoItem() || this.items.hasSlideshowItemContainingVideoSlide();
  }

  // Visual content duration, excluding audio: the accumulative duration of the
  // sequential backgrounds, raised by any non-background item whose bar extends
  // past the last background — items own their spans, so the longest item governs
  // when it lies beyond the background sequence. An empty page reports 0 — the
  // timeline should show its "add something" empty state.
  public getVisualDuration(): number {
    let backgroundsSum = 0;
    let maxItemEndTime = 0;
    for (const item of Object.values(this.items.itemsHashMap)) {
      if (item.isBackground) {
        backgroundsSum += item.getPageEndTime() - item.onPageStartTime;
      } else {
        maxItemEndTime = Math.max(maxItemEndTime, item.getPageEndTime());
      }
    }
    return Math.max(backgroundsSum, maxItemEndTime);
  }

  // Total page duration: visual duration raised by audio or subtitles if either
  // extends past the visible page, so playback runs long enough to play them out.
  // Shares `combinePageDuration` with the Redux-side `computePageDuration` so the clock's
  // playback max and the timeline UI's max are always derived from the same formula.
  public getDuration(): number {
    return combinePageDuration(this.getVisualDuration(), this.audioClips.getMaxOnPageEndTime(), this.items.getMaxSubtitleOnPageEndTime());
  }

  // The span the playback clock runs over. Webpage rotation holds a static page (data
  // duration 0) for a fixed span; that hold lives here, at playback level, and never in the
  // page data — mutating spans into the data would reclassify the page as a video
  // (timed background → isPageVideo) and surface playback UI a static poster must not get.
  // Item visibility keeps reading the data duration: 0 means "no timeline", always visible.
  public getPlaybackDuration(): number {
    const duration = this.getDuration();
    if (duration === 0 && this.poster.mode.isWebpage()) {
      return WEBPAGE_STATIC_PAGE_DURATION;
    }
    return duration;
  }

  public async toDataURL({maxDimension = 500, width, height}: ToDataURLOpts = {}): Promise<string> {
    let scale: number;
    const baseW = this.poster.width;
    const baseH = this.poster.height;

    if (width && height) {
      scale = Math.min(width / baseW, height / baseH);
    } else if (width) {
      scale = width / baseW;
    } else if (height) {
      scale = height / baseH;
    } else {
      const maxDesign = Math.max(baseW, baseH);
      scale = maxDimension / maxDesign;
    }
    if (scale <= 0.1) scale = 0.1;
    if (!Number.isFinite(scale)) scale = 1;

    const smallerClonedCanvas = this.getSmallerClonedFabricCanvas(scale);
    const clonedObjects = await this.getScaledClonesForCanvasObjects(scale);

    clonedObjects.forEach((clonedObject) => {
      smallerClonedCanvas.add(clonedObject);
    });

    const dataUrl = smallerClonedCanvas.toDataURL();

    void smallerClonedCanvas.dispose();

    return dataUrl;
  }

  private getSmallerClonedFabricCanvas(scale: number): Canvas {
    const scaledClonedCanvas = new Canvas(undefined, {
      width: this.poster.width * scale,
      height: this.poster.height * scale,
    });

    const activeBg = this.items.getActiveBackground();
    if (isColorBackground(activeBg)) {
      const fill = activeBg.getFillObject();
      if (typeof fill === 'string') {
        scaledClonedCanvas.backgroundColor = fill;
      }
    }

    return scaledClonedCanvas;
  }

  private onBeforeMouseDown(): void {
    this.poster.itemsMultiSelect.onMouseDownBefore();
    this.items.stopItemsAnimation();
  }

  private onMouseDown(e: CanvasEvents['mouse:down']): void {
    this.audioClips.unselectAudioPlaylist();
    this.transitions.clearBoundarySelection();
    this.onPotentialMarqueeStart(e);
    if (this.poster.mode.isWebpage()) {
      this.openClickableLinkOnItem(e);
      if (this.poster.isInitialized) {
        this.poster.togglePlayPause();
      }
    } else {
      if (this.poster.isPlaying()) {
        void this.poster.pause();
      }
      this.emptyBackgroundClickCandidate = !e.target && this.poster.getCurrentPageId() === this.hashedID && !this.items.getActiveBackground();
      if (!isEditorMobileVariant()) {
        this.poster.setCurrentPage(this.hashedID);
        if (!e.target) {
          this.showActivePageFlash();
        }
      }
    }
  }

  private onMouseUp(e: CanvasEvents['mouse:up']): void {
    this.groupDrillIn.onMouseUp(e);
    this.poster.itemsMultiSelect.onMouseUp(e);
    this.longPressInitiated = false;
    this.marqueePointerDownPoint = undefined;
    const wasDragSelect = this.isDragSelectInProgress;
    this.isDragSelectInProgress = false;
    const wasEmptyBackgroundClick = this.emptyBackgroundClickCandidate && !wasDragSelect && !e.target;
    this.emptyBackgroundClickCandidate = false;
    if (wasEmptyBackgroundClick && !this.items.getActiveBackground()) {
      void this.ensureAndSelectActiveBackground();
    }
  }

  /**
   * A marquee begins on empty canvas, the background, or a fully movement-locked item — a
   * locked body cannot be dragged, so a drag from it reads as a marquee while a plain click
   * still selects it (same rule the fabric fork applied in its own group selector). A press on
   * text that is being edited stays with text selection, and any other item under the pointer
   * gets clicked/dragged instead. Record the start point so a later drag can be detected; the
   * gesture is still just a click until the box is dragged past the threshold.
   */
  private onPotentialMarqueeStart(e: CanvasEvents['mouse:down']): void {
    const targetItem = e.target ? this.items.getItemForFabricObject(e.target) : undefined;
    const isPressOnMovementLockedBody = Boolean(e.target?.lockMovementX && e.target.lockMovementY && !(e.target instanceof Textbox && e.target.isEditing));
    this.marqueePointerDownPoint = !e.target || targetItem?.isBackground || isPressOnMovementLockedBody ? e.viewportPoint : undefined;
    this.isDragSelectInProgress = false;
  }

  /**
   * Whether the last mousedown landed on empty canvas or the background — the only presses a
   * marquee may begin from (see onPotentialMarqueeStart). The DOM marquee layer reads this as
   * its eligibility check instead of re-running fabric's hit test.
   */
  public hasPotentialMarqueeStart(): boolean {
    return this.marqueePointerDownPoint !== undefined;
  }

  /**
   * Driven by the DOM marquee layer when its drag passes the threshold (and again on release).
   * While a marquee is underway, hover borders and the empty-background hover stay hidden — on
   * every page the marquee box may sweep over, not just the one fabric saw the mousedown on.
   */
  public setMarqueeDragSelectInProgress(inProgress: boolean): void {
    this.isDragSelectInProgress = inProgress;
    if (inProgress) {
      this.clearHoveredItem();
      this.setEmptyBackgroundHover(false);
    }
  }

  private onMouseMove(e: CanvasEvents['mouse:move']): void {
    this.refreshEmptyBackgroundHover(e);
  }

  /**
   * Desktop affordance: when the pointer is over the empty area of the active page and that page
   * has no background, outline the whole page like an item's hover border so it reads as clickable
   * (a clean click adds and selects a default background). Suppressed on mobile, over an item, once
   * a background exists, or while a marquee drag is underway.
   */
  private refreshEmptyBackgroundHover(e: CanvasEvents['mouse:move']): void {
    const shouldShow =
      !isEditorMobileVariant() && !e.target && !this.isDragSelectInProgress && this.poster.getCurrentPageId() === this.hashedID && !this.items.getActiveBackground();
    this.setEmptyBackgroundHover(shouldShow);
  }

  private setEmptyBackgroundHover(shouldShow: boolean): void {
    if (this.canvasBackdrop.classList.contains(EMPTY_BACKGROUND_HOVER_CLASS) === shouldShow) {
      return;
    }
    this.canvasBackdrop.classList.toggle(EMPTY_BACKGROUND_HOVER_CLASS, shouldShow);
  }

  private openClickableLinkOnItem(e: CanvasEvents['mouse:down']): void {
    if (e.target) {
      const item = this.items.getItemForFabricObject(e.target);
      if (item?.hasClickableLink()) {
        redirectUser(item.clickableLink, false, false);
      }
    }
  }

  public getItemLinksMap(): Record<string, string> {
    const uidLinkMap: Record<string, string> = {};

    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if (item.hasClickableLink()) {
        uidLinkMap[item.uid] = item.clickableLink;
      }
    }
    return uidLinkMap;
  }

  public getStartTime(): number {
    return this.poster.pages.getStartTimeForPage(this.hashedID);
  }

  public getEndTime(): number {
    return this.poster.pages.getEndTimeForPage(this.hashedID);
  }

  public hasPlaybackEnded(): boolean {
    return this.clock.getCurrentTime() >= this.getPlaybackDuration() - PAGE_END_TIME_TOLERANCE_SECONDS;
  }

  private onBeforeSelectionCleared(fabricEvent: CanvasEvents['before:selection:cleared']): void {
    if (fabricEvent.e?.type === RESELECT_ITEM_EVENT_TYPE) {
      return;
    }

    for (const fabricObj of fabricEvent.deselected) {
      if (fabricObj instanceof ActiveSelection) {
        const activeObjects = fabricObj.getObjects();
        for (const activeObject of activeObjects) {
          this.items.getItem(activeObject.__PMWID)?.stopItemAnimation();
        }
      }
    }
  }

  private onSelectionCreated(fabricEvent: CanvasEvents['selection:updated']): void {
    if (fabricEvent.e?.type === RESELECT_ITEM_EVENT_TYPE) {
      return;
    }

    // A background is never part of a multi-selection; resolving it rebuilds the
    // selection, so group-propagation is skipped in that case.
    if (!this.enforceBackgroundNotInMultiSelection(fabricEvent) && this.shouldPropagateOnSelectionChange(fabricEvent)) {
      this.propagateSelectionToAllGroupMembers(fabricEvent);
    }

    this.toggleActiveSelectionLockIfNeeded();
    this.poster.updateSelectedItemPageId(this.hashedID);
    this.activeSelection.onSelectionCreated();
    // A timeline modifier-toggle builds a MIXED item+audio selection — only genuine
    // canvas selection changes tear down the audio half.
    if (!this.activeSelection.isTimelineSelectionChange()) {
      this.audioClips.unselectAudioPlaylist();
    }
    this.transitions.clearBoundarySelection();
    this.items.clearSelectedSubtitle();
    void getPosterUi().pasteStylesOnMouseDown();
    void getPosterUi().removeBgOnMouseDown();
    this.updateSelectedItemsInRedux();
    this.trackBackgroundEditOpenedFromCanvas(fabricEvent);
  }

  /**
   * Fires the background-edit GA event when the user opens a background's options by clicking it
   * directly on the canvas. Programmatic selections (timeline track, side-panel) carry no
   * originating DOM event and are tracked at their own call sites, so they are excluded here to
   * avoid double-counting.
   */
  private trackBackgroundEditOpenedFromCanvas(fabricEvent: CanvasEvents['selection:updated']): void {
    if (!fabricEvent.e) {
      return;
    }
    const selectedItems = this.getSelectedItems();
    if (selectedItems.length === 1 && selectedItems[0].isBackground) {
      trackPosterBuilderGA4Events(GA4EventName.BACKGROUND_MENU_OPEN, {[GA4EventParamName.SOURCE]: GA4EventSources.CANVAS});
    }
  }

  private shouldPropagateOnSelectionChange(fabricEvent: CanvasEvents['selection:updated']): boolean {
    if (fabricEvent.e?.type === DRILL_IN_EVENT_TYPE) {
      return false;
    }
    // We only skip group-propagation for drill-in: exactly one grouped item is now selected,
    // and the items just deselected are the other members of that same group.
    // Everything else returns true so groups stay all-or-nothing.
    const current = this.activeSelection.getActiveObjects();
    if (current.length !== 1) {
      return true;
    }
    const survivor = this.getItemForFabricObject(current[0]);
    if (!survivor?.groupId) {
      return true;
    }
    const deselected = fabricEvent.deselected ?? [];
    if (deselected.length === 0) {
      return true;
    }
    for (const obj of deselected) {
      if (this.getItemForFabricObject(obj)?.groupId !== survivor.groupId) {
        return true;
      }
    }
    return false;
  }

  /**
   * Keeps grouped items selected together. Selecting any group member selects the whole group,
   * and deselecting any group member deselects the whole group. Within a multi-selection,
   * the entire group joins or leaves the selection even if the user only touched one member.
   * The rebuild is tagged so the resulting selection events early-return at the handler
   * (see RESELECT_ITEM_EVENT_TYPE checks above), preventing re-entry into propagation.
   */
  private propagateSelectionToAllGroupMembers(fabricEvent: CanvasEvents['selection:updated']): void {
    const currentSelected = this.activeSelection.getActiveObjects();
    if (currentSelected.length === 0) {
      return;
    }

    const currentSet = new Set(currentSelected);

    const contractGroupIds = new Set<string>();
    const deselected = fabricEvent.deselected ?? [];
    for (const obj of deselected) {
      const item = this.getItemForFabricObject(obj);
      if (!item?.groupId) {
        continue;
      }
      const members = this.items.groups.getItemsByGroupId(item.groupId);
      if (members.some((m) => currentSet.has(m.fabricObject))) {
        contractGroupIds.add(item.groupId);
      }
    }

    const desired = new Set<FabricObject>();
    for (const obj of currentSelected) {
      const item = this.getItemForFabricObject(obj);
      if (item?.groupId && contractGroupIds.has(item.groupId)) {
        continue;
      }
      desired.add(obj);
      if (item?.groupId) {
        const members = this.items.groups.getItemsByGroupId(item.groupId);
        for (const m of members) {
          desired.add(m.fabricObject);
        }
      }
    }

    // z-order so Fabric reverse-walks _objects topmost-first during sub-target hit test
    const zIndex = new Map(this.fabricCanvas.getObjects().map((obj, i) => [obj, i]));
    const desiredSorted = Array.from(desired).sort((a, b) => (zIndex.get(a) ?? 0) - (zIndex.get(b) ?? 0));
    this.activeSelection.selectFabricObjects(desiredSorted, new MouseEvent(RESELECT_ITEM_EVENT_TYPE));
  }

  /**
   * A background is never part of a multi-selection. When a selection change mixes a
   * background with other items, the just-clicked object wins: shift-clicking the
   * background selects only it; adding or marquee-selecting items drops the background.
   * Returns true (and rebuilds the selection, tagged to avoid re-entry) when it acts.
   */
  private enforceBackgroundNotInMultiSelection(fabricEvent: CanvasEvents['selection:updated']): boolean {
    const activeObjects = this.activeSelection.getActiveObjects();
    if (activeObjects.length <= 1 || !activeObjects.some((obj) => this.getItemForFabricObject(obj)?.isBackground)) {
      return false;
    }

    const added = fabricEvent.selected ?? [];
    if (added.length === 1 && this.getItemForFabricObject(added[0])?.isBackground) {
      this.activeSelection.selectFabricObjects([added[0]], new MouseEvent(RESELECT_ITEM_EVENT_TYPE));
      return true;
    }

    const itemsWithoutBackground = activeObjects.filter((obj) => !this.getItemForFabricObject(obj)?.isBackground);
    const next = itemsWithoutBackground.length > 0 ? itemsWithoutBackground : [activeObjects[0]];
    this.activeSelection.selectFabricObjects(next, new MouseEvent(RESELECT_ITEM_EVENT_TYPE));
    return true;
  }

  private toggleActiveSelectionLockIfNeeded(): void {
    const activeSelection = this.activeSelection.getActiveObject();

    if (activeSelection instanceof ActiveSelection) {
      const activeObjects = activeSelection.getObjects();

      let lockAllObjects = false;

      for (const activeObject of activeObjects) {
        if (activeObject.isLocked()) {
          lockAllObjects = true;
          break;
        }
      }

      if (lockAllObjects) {
        activeSelection.set({
          lockMovementX: true,
          lockMovementY: true,
          lockScalingX: true,
          lockScalingY: true,
          lockRotation: true,
        });
      } else {
        activeSelection.set({
          lockMovementX: false,
          lockMovementY: false,
          lockScalingX: false,
          lockScalingY: false,
          lockRotation: false,
        });
      }
    }
  }

  private updateSelectedItemsInRedux(): void {
    const activeItemIds = this.getSelectedItemIds();
    this.poster.redux.updateActiveItemsOnPage({
      pageHashedId: this.hashedID,
      activeItemIds,
    });
    this.#clearHoveredItemIfSelected(activeItemIds);
  }

  /**
   * Selecting the hovered item must clear the stored hover, not just suppress its outline:
   * the hover uid otherwise sits hidden in Redux for the whole selection and reappears as a
   * phantom border the moment the uid drops out of the selection (e.g. duplicating hands the
   * selection to the clone). Fabric re-fires mouse:over on the next real mouse move, so a
   * genuine hover redraws immediately.
   */
  #clearHoveredItemIfSelected(activeItemIds: string[]): void {
    const currentHoveredId = window.PMW.redux.store.getState().posterEditor.hoveredItemId[this.hashedID];
    if (currentHoveredId !== undefined && activeItemIds.includes(currentHoveredId)) {
      this.clearHoveredItem();
    }
  }

  private onSelectionCleared(fabricEvent: CanvasEvents['selection:cleared']): void {
    if (fabricEvent.e?.type === RESELECT_ITEM_EVENT_TYPE) {
      return;
    }

    this.clearSelectedItemPageOnPoster();

    this.poster.itemsMultiSelect.onSelectionCleared();
    this.updateSelectedItemsInRedux();
    this.fabricCanvas.requestRenderAll();
  }

  private clearSelectedItemPageOnPoster(): void {
    if (this.poster.selectedItemPageId === this.hashedID) {
      this.poster.clearSelectedItemPageId();
    }
  }

  private shouldSkipContextMenu(): boolean {
    return this.poster.mode.isWebpage() || !!window.PMW.redux.store.getState().posterEditor.isMobileVariant;
  }

  private onContextMenuStopped(data: CanvasEvents['contextmenu:before']): void {
    if (this.shouldSkipContextMenu()) {
      return;
    }

    this.selectTargetItem(data.target);
    this.openContextualMenu(data);
  }

  private openContextualMenu(data: CanvasEvents['contextmenu:before']): void {
    const {scale} = this.poster.scaling;
    const mouseEvent = data.e as MouseEvent;

    getPosterUi().updatePopUpStateAndPosition({
      openPosterPopUpMenu: true,
      popUpPosition: {top: mouseEvent.pageY, left: mouseEvent.pageX},
      target: data.target,
      eventCanvasOffset: {top: mouseEvent.offsetY / scale, left: mouseEvent.offsetX / scale},
    });
  }

  private openTouchContextualMenu(data: HammerInput): void {
    if (data.pointerType === 'mouse' || this.poster.mode.isWebpage()) {
      return;
    }
    this.longPressInitiated = true;

    const activeObject = this.activeSelection.getActiveObject();
    if (isMobile && activeObject instanceof Textbox && activeObject.isEditing) {
      updateTextSelection(activeObject);
      handleTextSelectionPopupMenu(activeObject);
      this.fabricCanvas.requestRenderAll();
    } else if (window.PMW.redux.store.getState().posterEditor.isMobileVariant) {
      if (!activeObject?.__corner) {
        getPosterUi().openPosterEditorMoreOptionsModal();
      }
    } else {
      const {scale} = this.poster.scaling;
      getPosterUi().updatePopUpStateAndPosition({
        openPosterPopUpMenu: true,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore. data.srcEvent is the source event and HammerInput assumes it can only be TouchEvent | MouseEvent | PointerEvent, while in this case it's a fabric event which is why we aren't getting types.
        popUpPosition: {top: data.srcEvent.pageY as number, left: data.srcEvent.pageX as number},
        target: this.activeSelection.getActiveObject(),
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        eventCanvasOffset: {top: data.srcEvent.offsetY / scale, left: data.srcEvent.offsetX / scale},
      });
    }
  }

  private selectTargetItem(targetObject: FabricObject | undefined): void {
    if (targetObject && this.activeSelection.getActiveObjects().length < 2) {
      this.activeSelection.setActiveObject(targetObject);
    }
  }

  private addCanvasToPoster(opts: PageConstructorOpts = {}): void {
    let html = ``;
    const zoom = opts.zoom ?? 1;
    const showPageSliderContainer = !this.poster.mode.isWebpage();
    const scaledWidth = this.poster.width * zoom;
    const scaledHeight = this.poster.height * zoom;
    const canvasWidth = Math.round(scaledWidth);
    const canvasHeight = Math.round(scaledHeight);

    html = `<div class='${PAGE_CONTAINER_CLASS} js-canvas' data-id='${this.hashedID}'>
                    <div class="flexbox flex-row ${INNER_PAGE_CONTAINER_CLASS}">
                      <div class="flexbox flex-column">
                        <div
                        id='${this.hashedID}'
                        class='${CANVAS_BACKDROP_CLASS} _position-relative'
                        style="width: ${scaledWidth}px; height: ${scaledHeight}px;"
                        >
                            <canvas width='${canvasWidth}' height='${canvasHeight}'/>
                        </div>
                        ${showPageSliderContainer ? `<div class="page-slider-container" style="min-height: ${PAGE_SLIDER_CONTAINER_HEIGHT}px;"></div>` : ''}
                      </div>
                      <div>
                        <div class="page-right-side-options"></div>
                      </div>
                    </div>
                  </div>`;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html.trim();
    const htmlElement = tempDiv.firstChild as HTMLElement;
    this.poster.HTMLElement.insertBefore(htmlElement, opts.order !== undefined ? this.poster.HTMLElement.children[opts.order] : null);

    this.fabricCanvas = this.getFabricCanvas();
    const backDrop = Page.getCanvasBackdropForPageId(this.hashedID);
    const pageContainer = Page.getHTMLContainerForPageId(this.hashedID);
    if (!backDrop || !pageContainer) {
      throw new Error('Failed properly initialize html');
    }
    this.initCanvasContainer();

    this.pageContainer = pageContainer;
    this.canvasBackdrop = backDrop;
    this.setIsActivePage(this.poster.getCurrentPageId() === this.hashedID);

    this.updatePageVisibility();

    if (opts.zoom) {
      this.fabricCanvas.setZoom(zoom);
    }
    this.fabricCanvas.on('mouse:down:before', this.onBeforeMouseDown.bind(this));
    this.fabricCanvas.on('mouse:down', this.onMouseDown.bind(this));
    this.fabricCanvas.on('mouse:move', this.onMouseMove.bind(this));
    this.fabricCanvas.on('mouse:up', this.onMouseUp.bind(this));
    this.fabricCanvas.on('contextmenu:before', this.onContextMenuStopped.bind(this));
    this.fabricCanvas.on('path:created', (e) => {
      this.poster.drawing.onPathDrawn(e, this.hashedID);
    });
    this.fabricCanvas.on('after:touchstart', this.onAfterTouchStart.bind(this));
    this.fabricCanvas.on('before:render', this.clearContext.bind(this));

    if (isEditorMobileVariant()) {
      this.canvasContainer.classList.add('mobile-canvas-container');
    }
    this.initSelectionEvents();
    this.initObjectModificationEvents();
    this.initHoverEvents();
    this.initUpperCanvasElEvents();

    this.initRenderOnEachFrame();
    this.initRenderOnWindowFocus();
  }

  public initCanvasContainer(): void {
    if (this.fabricCanvas instanceof Canvas) {
      this.canvasContainer = this.fabricCanvas.elements.container;
    } else {
      this.canvasContainer = this.fabricCanvas.elements.lower.el;
    }
  }

  public updatePageVisibility(): void {
    if (this.poster.pages.isPageSwiping) {
      return;
    }

    if (getPosterUi().isSinglePageMode()) {
      if (this.poster.getCurrentPageId() === this.hashedID) {
        this.showPage();
        this.refreshVisibleCanvas();
      } else {
        this.hidePage();
      }
    } else {
      this.showPage();
    }
  }

  public showPage(): void {
    this.pageContainer?.classList.remove('_hidden');
  }

  public hidePage(): void {
    this.pageContainer?.classList.add('_hidden');
  }

  public removeHtml(): void {
    Page.getHTMLContainerForPageId(this.hashedID)?.remove();
  }

  public setPosterItemsModificationState = (modificationType: 'scaling' | 'moving' | 'rotating' | false): void => {
    this.poster.redux.setPosterItemsModificationState(modificationType, this.hashedID);
  };

  public setActiveItemControlsHideState = (updateShouldHideControlsProps: UpdateShouldHideControlsProps): void => {
    this.poster.redux.setActiveItemControlsHideState(updateShouldHideControlsProps);
  };

  private initObjectModificationEvents(): void {
    this.fabricCanvas.on('object:moving', this.onObjectsModifying.bind(this, 'moving'));
    this.fabricCanvas.on('object:rotating', this.onObjectsModifying.bind(this, 'rotating'));
    this.fabricCanvas.on('object:scaling', this.onObjectsModifying.bind(this, 'scaling'));
    this.fabricCanvas.on('object:modified', this.onObjectsModified.bind(this));
  }

  private initSelectionEvents(): void {
    this.fabricCanvas.on('before:selection:cleared', this.onBeforeSelectionCleared.bind(this));
    this.fabricCanvas.on('selection:updated', this.onSelectionCreated.bind(this));
    this.fabricCanvas.on('selection:created', this.onSelectionCreated.bind(this));
    this.fabricCanvas.on('selection:cleared', this.onSelectionCleared.bind(this));
  }

  private initHoverEvents(): void {
    this.fabricCanvas.on('object:removed', this.onObjectRemoved.bind(this));
    this.fabricCanvas.on('mouse:over', this.onObjectMouseOver.bind(this));
    this.fabricCanvas.on('mouse:out', this.onObjectMouseOut.bind(this));
  }

  /**
   * Marks the object mouse is over and renders canvas to draw the borders around it
   * @param {object} e
   * @private
   */
  private onObjectMouseOver(e: CanvasEvents['mouse:over']): void {
    if (this.isDragSelectInProgress) {
      return;
    }
    if (e.target && e.target.type !== 'activeSelection' && e.target.selectable) {
      this.mouseOverObject = e.target;

      if (this.isFabricObjectCurrentlySelected(e.target) || e.target instanceof ActiveSelection || this.poster.redux.areAnyPosterItemsModifying()) {
        return;
      }

      const item = this.getItemForFabricObject(e.target);
      if (!item) {
        return;
      }

      const currentHoveredId = window.PMW.redux.store.getState().posterEditor.hoveredItemId[this.hashedID];
      if (currentHoveredId === item.uid) {
        return;
      }

      this.poster.redux.updateHoveredItem({
        pageHashedId: this.hashedID,
        hoveredItemId: item.uid,
      });

      this.fabricCanvas.renderAll();
    }
  }

  /**
   * Clears the mouseOverObject if it was the object removed
   * @private
   */
  private onObjectRemoved(e: CanvasEvents['object:removed']): void {
    if (e.target && e.target === this.mouseOverObject) {
      this.mouseOverObject = null;

      this.poster.redux.updateHoveredItem({
        pageHashedId: this.hashedID,
        hoveredItemId: undefined,
      });
    }
  }

  /**
   * Marks the object mouse is over as null and renders canvas to clear any borders drawn
   * @private
   */
  private onObjectMouseOut(): void {
    this.clearHoveredItem();
    this.setEmptyBackgroundHover(false);
    this.fabricCanvas.renderAll();
  }

  private clearHoveredItem(): void {
    this.mouseOverObject = null;

    const currentHoveredId = window.PMW.redux.store.getState().posterEditor.hoveredItemId[this.hashedID];
    if (currentHoveredId === undefined) {
      return;
    }

    this.poster.redux.updateHoveredItem({
      pageHashedId: this.hashedID,
      hoveredItemId: undefined,
    });
  }

  private isFabricObjectCurrentlySelected(fabricObject: FabricObject): boolean {
    const selectedFabricObjects: FabricObject[] = this.getSelectedItems().map((selectedItem) => {
      return selectedItem.fabricObject;
    });

    for (const fabricObj of selectedFabricObjects) {
      if (fabricObj === fabricObject) {
        return true;
      }
    }

    return false;
  }

  private clearContext(): void {
    if (this.fabricCanvas instanceof Canvas && this.fabricCanvas.contextTop) {
      this.fabricCanvas.contextTopDirty = true;
    }
  }

  private async onObjectsModified(e: CanvasEvents['object:modified']): Promise<void> {
    await this.handlePageChangeOnObjectsModified(e);

    applyCanvasCursor(this.fabricCanvas as Canvas, 'default');
    this.setPosterItemsModificationState(false);
    this.poster.getCurrentPage().items.refreshAnimationDirections();
    this.poster.history.addPosterHistory();
    this.fire('modified');
    this.poster.redux.updateReduxData();
  }

  private async handlePageChangeOnObjectsModified(e: CanvasEvents['object:modified']): Promise<void> {
    if (!e.target) {
      return;
    }

    if (e.target instanceof ActiveSelection) {
      await this.handleActiveSelectionItemsPageChangeIfNeeded(e.target, e.transform?.original);
    } else {
      await this.handleItemPageChangedIfNeeded(e.target, e.transform?.original);
    }
  }

  private async handleItemPageChangedIfNeeded(selectedItemFabricObject: FabricObject, originalTransform?: {left: number; top: number}): Promise<void> {
    const item = this.getItemForFabricObject(selectedItemFabricObject);
    if (!item) {
      return;
    }

    if (!getPosterUi().isFabricObjectOnPageCanvas(selectedItemFabricObject, this.hashedID)) {
      const newPage = this.poster.pages.findPageContainingItem(selectedItemFabricObject);
      if (!newPage) {
        return;
      }

      const movedItemId = await getPosterUi().moveItemToDifferentPage(this, newPage, item);
      if (movedItemId) {
        if (item.groupId) {
          newPage.items.groups.autoDissolveIfTooSmall(item.groupId);
        }
        const newItem = newPage.items.getItem(movedItemId);
        if (newItem) {
          newPage.activeSelection.selectFabricObjects([newItem.fabricObject]);
        }
      } else if (originalTransform) {
        item.fabricObject.set({left: originalTransform.left, top: originalTransform.top});
        item.fabricObject.setCoords();
        this.fabricCanvas.requestRenderAll();
      }
    }
  }

  private async handleActiveSelectionItemsPageChangeIfNeeded(activeSelection: ActiveSelection, originalTransform?: {left: number; top: number}): Promise<void> {
    const activeObjects = activeSelection.getObjects();

    const itemMoves: {item: ItemType; newPage: Page}[] = [];
    let hasObjectsWithNoDestination = false;
    for (const obj of activeObjects) {
      const item = this.getItemForFabricObject(obj);
      if (!item || getPosterUi().isFabricObjectOnPageCanvas(obj, this.hashedID)) {
        continue;
      }
      const newPage = this.poster.pages.findPageContainingItem(obj);
      if (newPage) {
        itemMoves.push({item, newPage});
      } else {
        hasObjectsWithNoDestination = true;
      }
    }

    if (itemMoves.length === 0) {
      if (hasObjectsWithNoDestination) {
        this.clearSelection();
      }

      return;
    }

    if (!this.canAllItemsFitOnDestinationPages(itemMoves)) {
      if (originalTransform) {
        activeSelection.set({left: originalTransform.left, top: originalTransform.top});
        activeSelection.setCoords();
        this.fabricCanvas.requestRenderAll();
      }
      return;
    }

    const results = await Promise.all(itemMoves.map(({item, newPage}) => getPosterUi().moveItemToDifferentPage(this, newPage, item)));
    for (const {item, newPage} of itemMoves) {
      if (item.groupId) {
        newPage.items.groups.autoDissolveIfTooSmall(item.groupId);
      }
    }
    const movedUIDs = results.filter((uid): uid is string => uid !== undefined);

    this.poster.selectMovedItems(movedUIDs);
  }

  private canAllItemsFitOnDestinationPages(itemMoves: {item: ItemType; newPage: Page}[]): boolean {
    const destinationCounts = new Map<string, number>();
    for (const {newPage} of itemMoves) {
      const alreadyCounted = destinationCounts.get(newPage.hashedID) ?? 0;
      const wouldBeAdding = alreadyCounted + 1;
      if (!newPage.items.addItems.canFitAdditionalItems(wouldBeAdding)) {
        return false;
      }
      destinationCounts.set(newPage.hashedID, wouldBeAdding);
    }
    return true;
  }

  private onObjectsModifying(modificationType: 'scaling' | 'moving' | 'rotating', e: CanvasEvents['object:moving' | 'object:rotating' | 'object:scaling']): void {
    if (e.target) {
      setCanvasCursor(this.poster.getCurrentPage().fabricCanvas as Canvas, 'move');
      this.setPosterItemsModificationState(modificationType);
      if (e.target instanceof ActiveSelection) {
        const group = e.target;
        const activeObjects = group.getObjects();
        for (const activeObject of activeObjects) {
          const itemId = getFabricObjectUID(activeObject);
          const item = this.poster.getItemForId(itemId);
          if (item) {
            this.onItemModifying(item, modificationType);
          }
        }
      } else {
        const itemId = getFabricObjectUID(e.target);
        const item = this.poster.getItemForId(itemId);
        if (item) {
          this.onItemModifying(item, modificationType);
        }
      }
    }
  }

  private onItemModifying(item: ItemType, modificationType: 'scaling' | 'moving' | 'rotating'): void {
    switch (modificationType) {
      case 'moving':
        item.onMoving();
        return;
      case 'scaling':
        item.onScaling();
        return;
      case 'rotating':
        item.onRotating();
        return;
      default:
        throw new Error('Unhandled modificationType');
    }
  }

  private initRenderOnEachFrame(): void {
    if (!this.poster.mode.isGeneration()) {
      const render = (): void => {
        if (this.isDisposed || this.isSuspended) {
          return;
        }

        if (!this.poster.pages.hasPage(this.hashedID)) {
          return;
        }

        if (this.isPlaying && this.isVideo()) {
          this.fabricCanvas.renderAll();
          util.requestAnimFrame(render);
        } else {
          setTimeout(() => {
            util.requestAnimFrame(render);
          }, 300);
        }
      };
      util.requestAnimFrame(render);
    }
  }

  public suspend(): void {
    this.isSuspended = true;
    this.fabricCanvasHammerManager?.set({enable: false});
    void this.pause();
  }

  public resume(): void {
    void this.loadDeferredPageContent();

    if (!this.isSuspended) {
      return;
    }

    this.isSuspended = false;
    this.fabricCanvas.setZoom(this.poster.scaling.scale);
    this.fabricCanvasHammerManager?.set({enable: true});
    this.initRenderOnEachFrame();
  }

  private initRenderOnWindowFocus(): void {
    window.addEventListener('focus', this.boundOnWindowFocus);
  }

  private getFabricCanvas(): Canvas | StaticCanvas {
    if (this.poster.mode.isGeneration()) {
      return new StaticCanvas(this.getHTMLCanvas(), {
        renderOnAddRemove: false,
        skipControlsDrawing: true,
        backgroundColor: OPAQUE_CANVAS_COLOR,
      });
    }
    return new Canvas(this.getHTMLCanvas(), {
      hoverCursor: 'pointer',
      selection: true,
      uniScaleKey: 'shiftKey',
      controlsAboveOverlay: true,
      preserveObjectStacking: true,
      allowTouchScrolling: true,
      stopContextMenu: true,
      targetFindTolerance: isMobile ? 50 : 10,
      backgroundColor: OPAQUE_CANVAS_COLOR,
    });
  }

  private onCanvasDoubleTap(): void {
    const activeObject = this.activeSelection.getActiveObject();
    if (activeObject instanceof Textbox && activeObject.isEditing) {
      updateTextSelection(activeObject);
      handleTextSelectionPopupMenu(activeObject);
    } else {
      this.poster.redux.updateSidebarState(true);
    }
  }

  public async dispose(): Promise<void> {
    this.isDisposed = true;
    this.invalidatePauseTimeout();
    this.invalidateActiveFlashTimeout();
    this.clearSelection();
    this.clock.dispose();
    this.transitions.renderer.reset();
    this.renderAlignmentGuidelines.dispose();
    this.canvasPanOnTextEdit.dispose();
    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      item.onRemove();
    }
    for (const audioItem of Object.values(this.audioClips.audioItemsHashMap)) {
      audioItem.onRemove();
    }
    window.removeEventListener('focus', this.boundOnWindowFocus);
    Emitter.off('user:loggedIn', this.boundOnUserLoggedIn);
    this.removeHtml();
    for (const [eventName, eventFunction] of Object.entries(this.hammerEventFunctions)) {
      this.fabricCanvasHammerManager?.off(eventName, eventFunction as HammerListener);
    }
    this.fabricCanvasHammerManager?.destroy();
    await this.fabricCanvas.dispose();
  }

  private onAfterTouchStart(_e: CanvasEvents['after:touchstart']): void {
    this.poster.fire('fabricCanvas:touch:start');
  }

  private initUpperCanvasElEvents(): void {
    if (this.fabricCanvas instanceof Canvas) {
      this.fabricCanvasHammerManager = new Hammer.Manager(this.fabricCanvas.upperCanvasEl);
      const longPress = new Hammer.Press({
        event: 'longpress',
        time: 900,
      });
      const singletap = new Hammer.Tap({
        event: 'singletap',
      });

      this.fabricCanvasHammerManager.add([longPress, singletap]);
      this.fabricCanvasHammerManager.on('longpress', this.hammerEventFunctions.longpress);
      this.fabricCanvasHammerManager.on('singletap', this.hammerEventFunctions.singletap);

      if (isMobile) {
        const doubleTap = new Hammer.Tap({
          event: 'doubletap',
          taps: 2,
        });
        this.fabricCanvasHammerManager.add([doubleTap]);
        this.fabricCanvasHammerManager.on('doubletap', this.hammerEventFunctions.doubletap);
      }
    }
  }

  private onSingleTapOnCanvas(): void {
    const canvas = this.fabricCanvas as Canvas;
    const activeObject = canvas.getActiveObject();
    const activeItem = this.getSelectedItems()[0];
    if (activeObject && activeItem && activeObject instanceof Textbox && (activeItem.isText() || activeItem.isSlideshow()) && activeItem.clone && activeItem.clone.hiddenTextarea) {
      activeItem.clone.hiddenTextarea.focus();
    }
  }

  public static getCanvasBackdropForPageId(pageId: string): HTMLDivElement | undefined {
    return document.querySelector<HTMLDivElement>(`.pmw-page-container[data-id="${pageId}"] .${CANVAS_BACKDROP_CLASS}`) ?? undefined;
  }

  public static getHTMLContainerForPageId(pageId: string): HTMLDivElement | undefined {
    return document.querySelector<HTMLDivElement>(`.pmw-page-container[data-id="${pageId}"]`) ?? undefined;
  }

  public static getFirstCanvasContainerHTML(): HTMLElement | undefined {
    const elements = document.querySelectorAll<HTMLDivElement>('.pmw-page-container .canvas-container');
    return elements.length > 0 ? elements[0] : undefined;
  }

  public static getCanvasContainerHTMLForPageId(pageId: string): HTMLElement | undefined {
    return document.querySelector<HTMLDivElement>(`.pmw-page-container[data-id="${pageId}"] .canvas-container`) ?? undefined;
  }

  public static getPageRightSideHtmlContainerForPageId(pageId: string): HTMLElement | undefined {
    return document.querySelector<HTMLDivElement>(`.pmw-page-container[data-id="${pageId}"] .page-right-side-options`) ?? undefined;
  }

  public static getPageBottomHtmlContainerForPageId(pageId: string): HTMLElement | undefined {
    return document.querySelector<HTMLDivElement>(`.pmw-page-container[data-id="${pageId}"] .page-slider-container`) ?? undefined;
  }

  public static getCanvasBackdropHtmlContainerForPageId(pageId: string): HTMLElement | undefined {
    return document.querySelector<HTMLDivElement>(`.${PAGE_CONTAINER_CLASS}[data-id="${pageId}"] .${CANVAS_BACKDROP_CLASS}`) ?? undefined;
  }

  public getHTMLCanvas(): HTMLCanvasElement {
    const htmlCanvas = document.querySelector<HTMLCanvasElement>(`.pmw-page-container[data-id="${this.hashedID}"] canvas`);
    if (!htmlCanvas) {
      throw new Error('Canvas not found');
    }
    return htmlCanvas;
  }

  public getDuplicatedPageObject(): PageObject {
    return this.toObjectWithNewUids();
  }

  public toObject(): PageObject {
    const pageObject = this.getLivePageObject();
    if (this.deferredPageContentObject === null) {
      return pageObject;
    }
    return this.getPageObjectMergedWithDeferredContent(pageObject, this.deferredPageContentObject);
  }

  private getLivePageObject(): PageObject {
    return {
      isPlaying: this.isPlaying,
      currentTime: this.clock.getCurrentTime(),
      zoom: this.fabricCanvas.getZoom(),
      hashedID: this.hashedID,
      items: this.items.toObject(),
      audioClips: this.audioClips.toObject(),
      transitions: this.transitions.toObject(),
      isCustomDuration: this.isCustomDuration,
      isDeferredContentLoading: this.hasPendingDeferredPageContent(),
      lastModified: this.lastModified,
      templateData: this.templateData,
      galleryTemplateData: this.galleryTemplateData,
      appliedPalette: this.appliedPalette,
    };
  }

  /**
   * While page content is deferred, the live items/audioClips hold only what was added after load
   * (usually nothing). Serialization must still describe the full page, so the deferred content is
   * merged under any live content — otherwise a save or undo snapshot taken before the lazy load
   * finishes would wipe the not-yet-loaded items.
   */
  private getPageObjectMergedWithDeferredContent(pageObject: PageObject, deferredPageContentObject: PageObject): PageObject {
    const mergedItemsOrder = [...deferredPageContentObject.items.itemsOrder];
    for (const uid of pageObject.items.itemsOrder) {
      if (!mergedItemsOrder.includes(uid)) {
        mergedItemsOrder.push(uid);
      }
    }

    return {
      ...pageObject,
      items: {
        itemsHashMap: {...deferredPageContentObject.items.itemsHashMap, ...pageObject.items.itemsHashMap},
        itemsOrder: mergedItemsOrder,
        groups: {
          groupsHashMap: {...deferredPageContentObject.items.groups.groupsHashMap, ...pageObject.items.groups.groupsHashMap},
        },
      },
      audioClips: {
        audioItemsHashMap: {...deferredPageContentObject.audioClips.audioItemsHashMap, ...pageObject.audioClips.audioItemsHashMap},
      },
    };
  }

  public setDeferredPageContent(pageObject: PageObject): void {
    this.deferredPageContentObject = pageObject;
  }

  public hasPendingDeferredPageContent(): boolean {
    return this.deferredPageContentObject !== null;
  }

  public async loadDeferredPageContent(): Promise<void> {
    const deferredPageContentObject = this.deferredPageContentObject;
    if (deferredPageContentObject === null) {
      return;
    }
    this.deferredPageContentLoadPromise ??= this.applyDeferredPageContent(deferredPageContentObject);
    return this.deferredPageContentLoadPromise;
  }

  private async applyDeferredPageContent(deferredPageContentObject: PageObject): Promise<void> {
    if (this.isDisposed) {
      this.deferredPageContentObject = null;
      return;
    }

    // Merged (not raw deferred) content so items the user added while the page was still deferred
    // survive — items.updateFromObject removes anything missing from the incoming hash map.
    const mergedPageObject = this.getPageObjectMergedWithDeferredContent(this.getLivePageObject(), deferredPageContentObject);

    this.isApplyingDeferredPageContent = true;
    try {
      await this.updateFromObject(
        {items: mergedPageObject.items, audioClips: mergedPageObject.audioClips},
        {undoable: false, updateRedux: false, fireModifiedEvent: true, firePosterModifiedEvent: false, showAddItemLoadingToast: false}
      );
    } finally {
      this.deferredPageContentObject = null;
      this.isApplyingDeferredPageContent = false;
      // One Redux publish after the pending flag clears, so the mirrored isDeferredContentLoading
      // and the freshly loaded items land in the store together.
      this.poster.redux.updateReduxData();
    }
  }

  public toObjectWithNewUids(): PageObject {
    const itemUidMap = new Map<string, string>();
    return {
      ...this.toObject(),
      hashedID: getUniqueString(),
      items: this.items.toObjectWithNewUids(itemUidMap),
      transitions: remapPageTransitionsObjectUids(this.transitions.toObject(), itemUidMap),
    };
  }

  public async updateFromObject(
    pageObject: DeepPartial<PageObject> = {},
    {
      updateRedux = true,
      undoable = true,
      fireModifiedEvent = true,
      firePosterModifiedEvent = true,
      refreshActiveSelection = false,
      onItemAddFail = noop,
      showAddItemLoadingToast = true,
    }: PageUpdateFromObjectOpts = {}
  ): Promise<void> {
    // A content update (replace template, version restore, undo) arriving while this page's lazy
    // load is still pending must apply on top of the loaded content, not race or overwrite it.
    if (this.hasPendingDeferredPageContent() && !this.isApplyingDeferredPageContent && (pageObject.items !== undefined || pageObject.audioClips !== undefined)) {
      await this.loadDeferredPageContent();
    }

    const {items, zoom, isPlaying: _isPlaying, currentTime: _currentTime, isDeferredContentLoading: _isDeferredContentLoading, audioClips, transitions, ...pageObjectWithoutItems} = pageObject;
    this.copyVals({
      ...pageObjectWithoutItems,
    });

    if (zoom) {
      this.setZoom(zoom);
    }

    if (items) {
      await this.items.updateFromObject(items, {
        undoable: false,
        updateRedux: false,
        fireModifiedEvent: false,
        refreshActiveSelection,
        onItemAddFail,
        showAddItemLoadingToast,
      });
    }

    if (audioClips) {
      await this.audioClips.updateFromObject(audioClips, {
        updateRedux: false,
        fireModifiedEvent: false,
        undoable: false,
      });
    }

    // After items so revalidate() sees the reconciled background layout.
    if (transitions) {
      this.transitions.updateFromObject(transitions);
    }

    this.pageWatermark.refreshWatermark();
    if (undoable) {
      this.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.fire('modified', {firePosterModifiedEvent: firePosterModifiedEvent});
    }
    if (updateRedux) {
      this.poster.redux.updateReduxData();
    }
  }

  public setZoom(zoom: number, doRefreshVisibleCanvas = true): void {
    this.updateBackdropSizeForZoom(zoom);

    if (this.isSuspended) {
      return;
    }

    this.fabricCanvas.setZoom(zoom);
    if (doRefreshVisibleCanvas) {
      this.refreshVisibleCanvas();
    }
  }

  public selectAllUnlockedItems = (): void => {
    const fabricObjects = this.fabricCanvas.getObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }
    this.activeSelection.clearSelection();
    const objects: ItemFabricObject[] = [];
    for (const fabricObj of fabricObjects) {
      const item = this.items.getItem(fabricObj.__PMWID);
      if (item && !item.isBackground && !item.fabricObject.lockMovementX) {
        objects.push(fabricObj);
      }
    }

    this.activeSelection.selectFabricObjects(objects);
  };

  public selectAllItems = (): void => {
    this.selectItemsByIds(this.items.getNonBackgroundItemIdsInOrder());
  };

  public selectItemsByIds(ids: string[]): void {
    if (ids.length === 0) {
      return;
    }
    this.activeSelection.clearSelection();
    const objects: ItemFabricObject[] = [];
    for (const id of ids) {
      const item = this.items.getItem(id);
      if (item) {
        objects.push(item.fabricObject);
      }
    }
    this.activeSelection.selectFabricObjects(objects);
  }

  /**
   * Selects a background by id, routing through the normal fabric pipeline so
   * activeItemIds stays in sync. Pauses playback and seeks the playhead into the
   * background's span first — unless it is already the active background — so the
   * background is the visible, selectable object and a plain select doesn't fight
   * an in-flight pointer-up seek.
   */
  public async selectBackground(uid: string): Promise<void> {
    const background = this.items.getItem(uid);
    if (!background?.isBackground) {
      return;
    }
    await this.poster.pause();
    if (this.items.getActiveBackground()?.uid !== background.uid) {
      await this.seek(background.onPageStartTime);
    }
    this.activeSelection.setActiveObject(background.fabricObject);
  }

  /**
   * Ensures a background exists at the current time (creating a default solid-white one if the page
   * has none) and selects it so the background editor / color picker opens. Shared by the side-panel
   * "no background" swatch, the mobile background button, and the empty-canvas click affordance.
   */
  public async ensureAndSelectActiveBackground(): Promise<void> {
    this.setEmptyBackgroundHover(false);
    await this.items.ensureBackgroundAtCurrentTime();
    const background = this.items.getActiveBackground();
    if (background) {
      await this.selectBackground(background.uid);
    }
  }

  public getSelectedItems(): ItemType[] {
    const fabricObjects = this.activeSelection.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return [];
    }

    const selectedItems = [];
    for (const fabricObj of fabricObjects) {
      const item = this.getItemForFabricObject(fabricObj);
      if (item) {
        selectedItems.push(item);
      }
    }
    return selectedItems;
  }

  public getSelectedItemIds(): string[] {
    const fabricObjects = this.activeSelection.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return [];
    }

    const selectedItemIds = [];
    for (const fabricObj of fabricObjects) {
      const item = this.getItemForFabricObject(fabricObj);
      if (item) {
        selectedItemIds.push(item.uid);
      }
    }

    return selectedItemIds;
  }

  public getSelectedZIndexMovableItems(): ItemType[] {
    const fabricObjects = this.activeSelection.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return [];
    }

    const selectedItems = [];
    for (const fabricObj of fabricObjects) {
      const item = this.getItemForFabricObject(fabricObj);
      if (item?.canMoveInZIndex()) {
        selectedItems.push(item);
      }
    }
    return selectedItems;
  }

  public getFilteredExistingViews(views: (FabricObject | Group)[]): (FabricObject | Group)[] {
    const filteredViews = [];
    for (const view of views) {
      if (this.fabricCanvas.getObjects().includes(view)) {
        filteredViews.push(view);
      }
    }
    return filteredViews;
  }

  public getItemForFabricObject(fabricObj: FabricObject): ItemType | undefined {
    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if (item.doesfabricObjBelongtoItem(fabricObj)) {
        return item;
      }
    }
    return undefined;
  }

  public getStreamingItems(): ItemType[] {
    const streamingItems = [];
    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if (item.isStreamingMediaItem() || isVideoBackground(item)) {
        streamingItems.push(item);
      }
    }
    return streamingItems;
  }

  public hasItem(uid: string): boolean {
    return this.items.itemsHashMap[uid] !== undefined;
  }

  public getActiveBackground(time?: number): ItemType | undefined {
    return this.items.getActiveBackground(time);
  }

  public refreshFabricCanvasBackground(time?: number): void {
    const nextBackgroundColor = this.items.shouldCanvasBackgroundBeTransparent(time) ? TRANSPARENT_CANVAS_COLOR : OPAQUE_CANVAS_COLOR;
    if (this.fabricCanvas.backgroundColor === nextBackgroundColor) {
      return;
    }
    this.fabricCanvas.backgroundColor = nextBackgroundColor;
    this.fabricCanvas.requestRenderAll();
  }

  // Duplicates a background and selects the clone, so duplicating leaves the user
  // on the new background (mirrors how the canvas selects a duplicated item).
  public async duplicateBackgroundAndSelect(uid: string): Promise<void> {
    const duplicate = await this.items.duplicateBackground(uid);
    if (duplicate) {
      await this.seek(duplicate.onPageStartTime);
      this.activeSelection.setActiveObject(duplicate.fabricObject);
    }
  }

  /**
   * Paints the canvas backdrop opaque white. Used by exports that can't carry an alpha channel
   * (jpeg/gif/mp4/pdf): the backdrop is set once and stays fixed for the whole output, so any
   * transparent region must flatten onto white rather than the black that #00000000 encodes to.
   * Unconditional on purpose — an opaque background simply covers this, and a transparent
   * background anywhere in a sequence still needs the white fallback even when the background
   * under the playhead at export time happens to be opaque.
   */
  public showWhiteCanvasBackground(): void {
    this.fabricCanvas.backgroundColor = OPAQUE_CANVAS_COLOR;
  }

  /**
   * The live face of `isPageVideo` — the ONE rule deciding timeline visibility, playback,
   * and duration trust, so they can never disagree. A page where this is false has no real
   * duration: whatever uniform [0, D] spans its items carry are automatic backfill left
   * behind by a removed time base (or the static default 0), not user intent, so
   * duration-sensitive flows must not trust them — e.g. a static page whose items still
   * end at a removed video's 10s would otherwise loop the next 5s video out to 10s.
   * Authored timings (staggered starts/ends) DO count, via `doItemsCarryAuthoredTimings`,
   * and so does ANY slideshow — MigrateToVersion73 dissolved the historically static
   * single-slide case into plain items, so a slideshow always plays.
   */
  public isVideo(): boolean {
    return isPageVideo(this.toObject());
  }

  public isTransparent(): boolean {
    return this.items.hasAnyTransparentBackground();
  }

  public isPremium(): boolean {
    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if (item.isPremium()) {
        return true;
      }
    }
    return false;
  }

  public async stop(): Promise<void> {
    if (!this.isPlaying) {
      return;
    }
    this.clock.stop();
    this.updateIsPlaying(false);
    this.audioClips.pause();

    const promises = [];
    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if ('stop' in item) {
        promises.push(item.stop());
      }
    }

    await Promise.all(promises);
  }

  public async playPage(): Promise<void> {
    this.clock.play();
    this.updateIsPlaying(true);
    this.audioClips.play();

    const promises = [];

    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if ('play' in item) {
        promises.push(item.play());
      }
    }

    await Promise.all(promises);
  }

  public updateIsPlaying(val: boolean): void {
    this.isPlaying = val;
    this.poster.redux.updatePageData({
      pageHashId: this.hashedID,
      obj: {
        isPlaying: val,
      },
    });
  }

  public async clampCurrentTime(): Promise<void> {
    if (!this.isVideo()) {
      await this.seek(0);
      return;
    }

    if (this.clock.getCurrentTime() > this.getDuration()) {
      await this.seek(this.getDuration());
    }
  }

  /**
   * Re-establishes the `0 <= currentTime <= duration` invariant after a mutation that may have
   * shortened the page (e.g. dragging the duration-defining audio clip left). Synchronous so it can
   * run inside `commitPosterMutation` before the poster is serialized to Redux: `clock.setCurrentTime`
   * clamps to the live duration and fires `time:updated` (so items/animations resync), and because
   * `toObject()` reads `clock.getCurrentTime()`, the serialized playhead is already valid. No-op when
   * the playhead is still within bounds, so it costs a single comparison on the common path.
   *
   * The bound is the PLAYBACK duration, the same one the clock runs on: during a webpage static
   * hold the data duration is 0 while the clock legitimately sits mid-hold, and any Redux publish
   * — a sibling page finishing its deferred load, for one — would otherwise reset the playhead and
   * restart the hold, leaving rotation stuck on that page.
   */
  public reconcilePlayheadToDuration(): void {
    const playbackDuration = this.getPlaybackDuration();
    if (this.clock.getCurrentTime() <= playbackDuration) {
      return;
    }
    this.clock.setCurrentTime(playbackDuration);
    this.onSeekPage();
    this.fabricCanvas.requestRenderAll();
  }

  public async seek(time: number): Promise<void> {
    this.clock.setCurrentTime(time);
    await this.audioClips.seekToPageTime(time);
    this.onSeekPage();
    this.fire('seek', time);
    this.fabricCanvas.requestRenderAll();
  }

  /**
   * Seeks to the given time, ensuring only one seek runs at a time.
   * If called while a seek is in progress, the latest requested time is
   * queued and executed once the current seek completes.
   */
  public async seekThrottled(time: number): Promise<void> {
    this.#pendingSeekTime = time;

    if (this.#isSeeking) {
      return;
    }

    this.#isSeeking = true;
    try {
      await this.#drainThrottledSeek();
    } finally {
      this.#isSeeking = false;
    }
  }

  async #drainThrottledSeek(): Promise<void> {
    if (this.#pendingSeekTime === undefined) {
      return;
    }

    const seekTime = this.#pendingSeekTime;
    this.#pendingSeekTime = undefined;
    await this.seek(seekTime);
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 200);
    });
    await this.#drainThrottledSeek();
  }

  public onSeekPage(): void {}

  public async pauseOrPlayBetweenStartAndEndTime(start: number, end: number): Promise<void> {
    if (this.isPlaying) {
      await this.pause();
      return;
    }
    await this.playBetweenStartAndEndTime(start, end);
  }

  /**
   * Always (re)starts a bounded preview — unlike pauseOrPlayBetweenStartAndEndTime's toggle,
   * an in-flight preview is cut and the new range plays immediately, and the previous
   * preview's scheduled pause is cancelled so it can't cut the new one short. For rapid
   * option cycling in editors (e.g. switching transition types mid-preview).
   */
  public async playBetweenStartAndEndTime(start: number, end: number): Promise<void> {
    this.invalidatePauseTimeout();
    if (this.isPlaying) {
      await this.pause();
    }

    await this.seek(start);

    try {
      await this.playPage();

      const duration = (end - start) * 1000;
      this.pauseTimeoutId = setTimeout(() => {
        void (async (): Promise<void> => {
          await this.pause();
          this.pauseTimeoutId = null;
        })();
      }, duration);
    } catch (e) {
      console.error(e);
      await this.pause();
    }
  }

  public invalidatePauseTimeout(): void {
    if (this.pauseTimeoutId !== null) {
      clearTimeout(this.pauseTimeoutId);
      this.pauseTimeoutId = null;
    }
  }

  private invalidateActiveFlashTimeout(): void {
    if (this.activeFlashTimeoutId !== null) {
      clearTimeout(this.activeFlashTimeoutId);
      this.activeFlashTimeoutId = null;
    }
  }

  public setIsActivePage(isActive: boolean): void {
    if (!this.pageContainer) {
      return;
    }
    this.pageContainer.classList.toggle(ACTIVE_PAGE_CLASS, isActive && this.poster.pages.hasMultiplePages());
  }

  private showActivePageFlash(): void {
    if (!this.poster.pages.hasMultiplePages()) {
      return;
    }

    // Remove class first to restart animation if already running (e.g. rapid clicks)
    this.canvasBackdrop.classList.remove(ACTIVE_PAGE_FLASH_CLASS);
    // Force reflow so the browser restarts the CSS animation from the beginning
    void this.canvasBackdrop.offsetWidth;
    this.canvasBackdrop.classList.add(ACTIVE_PAGE_FLASH_CLASS);

    this.invalidateActiveFlashTimeout();
    this.activeFlashTimeoutId = setTimeout(() => {
      this.canvasBackdrop.classList.remove(ACTIVE_PAGE_FLASH_CLASS);
      this.activeFlashTimeoutId = null;
    }, 1000);
  }

  public async pause(): Promise<void> {
    this.invalidatePauseTimeout();
    if (!this.isPlaying) {
      return;
    }
    this.clock.pause();
    this.updateIsPlaying(false);

    this.audioClips.pause();

    const promises = [];
    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if ('pause' in item) {
        promises.push(item.pause());
      }
    }

    await Promise.all(promises);
  }

  public getCanvasCornerPoints(): CornerPoints {
    const {scale} = this.poster.scaling;
    return {
      tl: {
        x: 0,
        y: 0,
      },
      tr: {
        x: this.fabricCanvas.width / scale,
        y: 0,
      },
      br: {
        x: this.fabricCanvas.width / scale,
        y: this.fabricCanvas.height / scale,
      },
      bl: {
        x: 0,
        y: this.fabricCanvas.height / scale,
      },
    };
  }

  public clearSelection(): void {
    this.activeSelection.clearSelection();
  }

  public getColorsOnPage(): string[] {
    let colors: RGBA[] = [];
    const itemsOnPage = this.items.getItems();
    for (const item of itemsOnPage) {
      colors = [...item.getColors(), ...colors] as RGBA[];
    }

    const colorsOnDesign: string[] = [];
    for (const eachColor of colors) {
      colorsOnDesign.push(rgbaToHex(eachColor, false));
    }

    const uniqueColorsOnDesign = colorsOnDesign.filter((color, index) => {
      return colorsOnDesign.indexOf(color) === index;
    });

    return uniqueColorsOnDesign.sort((a, b) => {
      return tinycolor(a).getLuminance() - tinycolor(b).getLuminance();
    });
  }

  public getFontsOnPage(withVariation = false): string[] {
    const items = Object.values(this.items.itemsHashMap);
    const fonts: string[] = [];

    for (const eachItem of items) {
      fonts.push(...eachItem.getFonts(withVariation));
    }

    return fonts.filter((font, index) => {
      return fonts.indexOf(font) === index;
    });
  }

  public isSingleItemSelected(): boolean {
    const selectedItems = this.getSelectedItems();
    if (!selectedItems) {
      return false;
    }

    return selectedItems.length === 1;
  }

  public validatePremiumUserAccess(): boolean {
    return this.isPremium() && window.PMW.getUserPremiumLevel() === USER_PREMIUM_LEVELS.PAYG;
  }

  public async replacePageWithObject(pageObjectToReplaceWith: PageObject, undoable = false): Promise<void> {
    await this.updateFromObject(
      {
        items: pageObjectToReplaceWith.items,
      },
      {undoable}
    );
  }

  public hasAnimation(): boolean {
    return this.items.pageItemHasAnimation();
  }

  public hasIntroAnimation(): boolean {
    return this.items.pageItemHasIntroAnimation();
  }

  public hasOutroAnimation(): boolean {
    return this.items.pageItemHasOutroAnimation();
  }

  public applyIntroAnimationToItems(
    animation: DeepPartial<AnimationObject>,
    {undoable = true, fireModifiedEvent = true, replayPosterOnUpdateDone = false}: UpdateFromObjectOpts = {}
  ): void {
    // Captured before the loop applies the animation: once any item carries it, isVideo()
    // already reads true and the static→video conversion below could never fire.
    const pageWasVideoBeforeUpdate = this.isVideo();
    // Captured before giveNonVideoPageAnimationPlaybackSpan backfills default spans.
    const itemTimingsWereAuthoredBeforeApply = doItemsCarryAuthoredTimings(this.toObject());

    Object.keys(this.items.itemsHashMap).forEach((key) => {
      const item = this.items.itemsHashMap[key];
      const type = animation.type ?? item.introAnimation.type;
      if (isAnimationApplicableOnItem(item, type)) {
        item.introAnimation.copyVals({type, ...animation});
      } else if (animation.type !== undefined) {
        item.introAnimation.copyVals({type: AnimationType.NONE});
      }
    });

    // Before the stagger seeding below, which derives its times from the page's span.
    this.giveNonVideoPageAnimationPlaybackSpan(pageWasVideoBeforeUpdate);

    // Visual (background) duration, not getDuration() — the latter includes audio/subtitles,
    // which would push an animated item's timing into the audio-only tail past the visual span.
    const introResult = calculateIntroStartTimes(this.items.itemsHashMap, this.fabricCanvas, this.getVisualDuration(), this.items.isVideoItemUsedAsBackground());
    // Applying a page animation seeds the entrance stagger ONCE, into a page whose item timing
    // is still the full-span default. Timing already authored on the timeline (or seeded by an
    // earlier apply) is kept — after the first application, animation type and timing are
    // orthogonal, and this apply is the ONLY writer of stagger (canvas edits never recompute it).
    if (!itemTimingsWereAuthoredBeforeApply) {
      for (const [uid, startTime] of Object.entries(introResult.startTimes)) {
        if (this.items.itemsHashMap[uid] && isAnimationDurationApplicableOnItem(this.items.itemsHashMap[uid])) {
          this.items.itemsHashMap[uid].onPageStartTime = startTime;
        }
      }
    }
    for (const [uid, direction] of Object.entries(introResult.directionUpdates)) {
      this.items.itemsHashMap[uid]?.introAnimation.copyVals({direction});
    }

    if (undoable) {
      this.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.fire('modified');
    }
    this.poster.redux.updateReduxData();
    if (replayPosterOnUpdateDone) {
      void this.poster.replayPage(this.hashedID);
    }
  }

  public applyOutroAnimationToItems(
    animation: DeepPartial<AnimationObject>,
    {undoable = true, fireModifiedEvent = true, replayPosterOnUpdateDone = false}: UpdateFromObjectOpts = {}
  ): void {
    // Captured before the loop applies the animation: once any item carries it, isVideo()
    // already reads true and the static→video conversion below could never fire.
    const pageWasVideoBeforeUpdate = this.isVideo();
    // Captured before giveNonVideoPageAnimationPlaybackSpan backfills default spans.
    const itemTimingsWereAuthoredBeforeApply = doItemsCarryAuthoredTimings(this.toObject());

    Object.keys(this.items.itemsHashMap).forEach((key) => {
      const item = this.items.itemsHashMap[key];
      const type = animation.type ?? item.outroAnimation.type;
      if (isAnimationApplicableOnItem(item, type)) {
        item.outroAnimation.copyVals({type, ...animation});
      } else if (animation.type !== undefined) {
        item.outroAnimation.copyVals({type: AnimationType.NONE});
      }
    });

    // Before the outro stagger seeding below, which derives its end times from the page's span.
    this.giveNonVideoPageAnimationPlaybackSpan(pageWasVideoBeforeUpdate);

    // Visual (background) duration, not getDuration() — the latter includes audio/subtitles,
    // which would push an animated item's timing into the audio-only tail past the visual span.
    const outroResult = calculateOutroEndTimes(this.items.itemsHashMap, this.fabricCanvas, this.getVisualDuration());
    // Same one-time seeding rule as applyIntroAnimationToItems: only default full-span timing
    // receives the staggered exit ends; authored timeline timing is never rewritten.
    if (!itemTimingsWereAuthoredBeforeApply) {
      for (const [uid, endTime] of Object.entries(outroResult.endTimes)) {
        if (this.items.itemsHashMap[uid] && isAnimationDurationApplicableOnItem(this.items.itemsHashMap[uid])) {
          this.items.itemsHashMap[uid].setOnPageEndTime(endTime);
        }
      }
    }
    for (const [uid, direction] of Object.entries(outroResult.directionUpdates)) {
      this.items.itemsHashMap[uid]?.outroAnimation.copyVals({direction});
    }

    if (undoable) {
      this.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.fire('modified');
    }
    this.poster.redux.updateReduxData();
    if (replayPosterOnUpdateDone) {
      void this.stop();
      void this.seek(this.#getOutroReplaySeekTime());
      void this.playPage();
    }
  }

  /**
   * Animating a page that was not a video (per isVideo, captured before the animation applied)
   * converts it to one, but animations bring no duration of their own — and the page's stored
   * item times are untrusted leftovers there — so re-fit its items to the default playback span,
   * mirroring the static→video conversion on video/slideshow/audio adds. A page that was already
   * a video is never touched. Callers commit history themselves, so the animation and the refit
   * land in one undo step.
   */
  public giveNonVideoPageAnimationPlaybackSpan(pageWasVideoBeforeUpdate: boolean): void {
    if (pageWasVideoBeforeUpdate || !this.isVideo()) {
      return;
    }
    this.items.refitAllItemsToConvertedPageSpan(DEFAULT_NON_VIDEO_PAGE_DURATION);
  }

  #getOutroReplaySeekTime(): number {
    const END_TIME_LEEWAY = 4;
    const DELAY_FACTOR = 0.2;
    const pageDuration = this.getDuration();
    let minEndTime = pageDuration;
    Object.keys(this.items.itemsHashMap).forEach((key) => {
      const item = this.items.itemsHashMap[key];
      const itemEndTime = item.getPageEndTime();
      if (itemEndTime > pageDuration - END_TIME_LEEWAY) {
        minEndTime = Math.min(minEndTime, itemEndTime - getAnimationTime(item.outroAnimation.type, item.outroAnimation.speed));
      }
    });
    return Math.max(0, minEndTime - DELAY_FACTOR);
  }

  private onPageModified({firePosterModifiedEvent = true}: PageModifiedEvent): void {
    this.lastModified = new Date();
    // A modification can change which background is active or its transparency; keep the backdrop in sync.
    this.refreshFabricCanvasBackground();

    if (firePosterModifiedEvent) {
      this.poster.fire('modified');
    }
  }

  // Repaint the canvas backdrop as the clock moves so it tracks whichever background is active at
  // `time`. Cheap: refreshFabricCanvasBackground no-ops unless the backdrop colour actually changes.
  private onPageTimeUpdated(time: number): void {
    this.refreshFabricCanvasBackground(time);
    this.transitions.renderer.setFrame(time);
  }

  private async getScaledClonesForCanvasObjects(scale: number): Promise<FabricObject[]> {
    const originalObjects = this.fabricCanvas.getObjects();

    return Promise.all(
      originalObjects.map(async (obj) => {
        const clone = await cloneAsAlignedImage(obj);
        return this.applyScaleToClone(clone, scale);
      })
    );
  }

  private applyScaleToClone(clone: FabricObject, scale: number): FabricObject {
    const center = clone.getCenterPoint();

    clone.set({
      scaleX: (clone.scaleX ?? 1) * scale,
      scaleY: (clone.scaleY ?? 1) * scale,
    });

    clone.setPositionByOrigin(new Point(center.x * scale, center.y * scale), 'center', 'center');

    return clone;
  }

  private updateBackdropSizeForZoom(zoom: number): void {
    this.canvasBackdrop.style.width = `${this.poster.width * zoom}px`;
    this.canvasBackdrop.style.height = `${this.poster.height * zoom}px`;
  }
}
